import { DEFAULT_AXIS_LABEL_FONT_SIZE } from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { XAXisOption, YAXisOption } from "echarts/types/dist/shared";
import { createEntityState } from "entities/charts/factory";
import { ChartOption } from "entities/charts/options";
import _zip from "lodash/zip";
import { EvaChartStates } from "types/factory";
import { uid } from "utils";

import { Format } from "models";
import { Annotation } from "models/chart";

import { AXIS_NAME_TEXT_STYLE } from "components/multiphase-chart/constants/shared.constants";

import { createChartLocks } from "../locks";
import { mpcYAxisLocks } from "../locks/mpcLocks";
import { DEFAULT_Z_INDEX, STATISTICS_Z_INDEX } from "./constants";
import {
  buildProductStyleMap,
  buildYAxisIndexRemap,
  getLineStyleForSeries,
  getYAxisPosition,
  inferAxisProduct
} from "./mpcTranslatorHelpers";
import { applyProductLegend } from "./mpcProductLegend";
import { getEffectiveSeriesColor } from "./getEffectiveSeriesColor";
import { updateOptionsWithTypeWellsSeries } from "./updateOptionsWithTypeWellSeries";
import {
  createLogAxisLabelFormatter,
  formatChartTitleWithSubscript,
  getDefaultYAxisLogOptions,
  getTimeSeriesMinMax,
  isStatisticsLabel
} from "./utils";

function getMarkLineData(annotations: Annotation[], yMin: number) {
  if (!annotations) {
    return {};
  }
  const marklineData = annotations.map((item, index) => {
    return [
      {
        xAxis: item.xValue,
        yAxis: yMin,
        label: {
          show: true,
          formatter: () => item.comment
        }
      },
      {
        xAxis: item.xValue,
        y: `${100 + 25 * index}`
      }
    ];
  });
  return {
    lineStyle: {
      type: "dashed"
    },
    symbol: [],
    data: marklineData
  };
}

export function translateRateDateResponseToOptions(states: Partial<EvaChartStates>) {
  const {
    title,
    layout,
    series,
    typeWellSeries = [],
    logAxis,
    overrideSeries = true,
    chartType,
    product,
    entityKind,
    multiProduct,
    getSeriesStyle
  } = states;

  if (series.length === 0 && typeWellSeries.length === 0) {
    return {};
  }

  const primaryProducts = multiProduct?.properties?.primaryProducts || [];
  const secondaryProducts = multiProduct?.properties?.secondaryProducts || [];
  // Use Set to ensure unique products only
  const allProducts = [...new Set([...primaryProducts, ...secondaryProducts])];

  const productStyleMap = buildProductStyleMap(multiProduct);

  // Helper function to get min/max for series by axis index
  const getAxisMinMax = (seriesData, axisIndex, axisType) => {
    const filteredSeries = seriesData.filter(
      (s) => (axisType === "x" ? s.xAxisIndex ?? 0 : s.yAxisIndex ?? 0) === axisIndex
    );
    return filteredSeries.length > 0
      ? getTimeSeriesMinMax(filteredSeries, Format.Month)
      : null;
  };

  // Calculate min/max for default axes (axis index 0)
  const defaultAxisSeries = series.filter(
    (s) => (s.xAxisIndex ?? 0) === 0 && (s.yAxisIndex ?? 0) === 0
  );
  const { xMin, xMax, yMin, yMax } =
    defaultAxisSeries.length > 0
      ? getTimeSeriesMinMax(defaultAxisSeries, Format.Month)
      : getTimeSeriesMinMax(series, Format.Month); // fallback for non-multiproduct mode

  const {
    lockedXMax,
    lockedXMin,
    lockedYMax,
    lockedYMin,
    lockedXMinType,
    lockedXMaxType,
    lockedYMinType,
    lockedYMaxType
  } = createChartLocks({
    type: EvaChart.RateDate,
    states
  });

  const xAxis: Partial<XAXisOption> = {
    name: formatChartTitleWithSubscript(layout.xAxis.title),
    nameGap: 27,
    type: "time",
    max: lockedXMax,
    min: lockedXMin
  };

  const yAxis: Partial<YAXisOption> = {
    name: formatChartTitleWithSubscript(layout?.yAxis?.title) || "",
    nameGap: 43,
    nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
    min: lockedYMin,
    max: lockedYMax
  };

  // X axis: rate/date has at most one secondary x-axis, so no mapping needed
  const multiXAxis =
    multiProduct?.active && layout?.xAxis2?.[0]
      ? (() => {
          const axis = layout.xAxis2[0];
          const axisMinMax = getAxisMinMax(series, 0, "x");
          const axisXMin = axisMinMax?.xMin ?? xMin;
          const axisXMax = axisMinMax?.xMax ?? xMax;
          return [
            {
              id: 0,
              type: "time",
              min: lockedXMin,
              max: lockedXMax,
              name: formatChartTitleWithSubscript(axis.title),
              nameLocation: "middle",
              nameGap: 30,
              nameTextStyle: AXIS_NAME_TEXT_STYLE,
              axisLabel: { fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE },
              offset: 0,
              position: "bottom",
              axisLine: { show: true, onZero: false },
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              useY: false, // useY is managed at hd chart
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              lock: {
                show: true,
                min: {
                  value: lockedXMin ?? axisXMin.value,
                  type: lockedXMinType ?? axisXMin.type,
                  format: axisXMin.format,
                  override: true
                },
                max: {
                  value: lockedXMax ?? axisXMax.value,
                  type: lockedXMaxType ?? axisXMax.type,
                  format: axisXMax.format,
                  override: true
                }
              }
            }
          ];
        })()
      : null;

  const multiYAxis =
    multiProduct?.active && layout?.yAxis2?.length
      ? layout.yAxis2.map((axis, i) => {
          const axisMinMax = getAxisMinMax(series, i, "y");
          const axisYMin = axisMinMax?.yMin ?? yMin;
          const axisYMax = axisMinMax?.yMax ?? yMax;
          const seriesForThisAxis = series.filter((s) => (s.yAxisIndex ?? 0) === i);

          // Infer the product for this axis from series labels.
          const axisProduct = inferAxisProduct(allProducts, seriesForThisAxis, product);

          const {
            lockedYMin: axisLockedYMin,
            lockedYMax: axisLockedYMax,
            lockedYMinType: axisLockedYMinType,
            lockedYMaxType: axisLockedYMaxType
          } = mpcYAxisLocks(axisProduct, seriesForThisAxis, states);

          const axisPosition = getYAxisPosition(
            primaryProducts,
            secondaryProducts,
            seriesForThisAxis
          );

          const isLogY = logAxis?.properties?.y?.active;

          return {
            id: i,
            type: isLogY ? "log" : "value",
            name: formatChartTitleWithSubscript(axis.title),
            nameLocation: "middle",
            nameGap: 30,
            nameTextStyle: AXIS_NAME_TEXT_STYLE,
            axisLabel: { fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE } as Record<
              string,
              unknown
            >,
            position: axisPosition,
            offset: 0,
            min: axisLockedYMin,
            max: axisLockedYMax,
            axisLine: { show: true, onZero: false },
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            lock: {
              show: true,
              productKey: axisProduct,
              min: {
                value: Math.floor(axisLockedYMin ?? axisYMin?.value ?? yMin.value),
                type: axisLockedYMinType ?? axisYMin?.type ?? yMin.type,
                override: true
              },
              max: {
                value: Math.floor(axisLockedYMax ?? axisYMax?.value ?? yMax.value),
                type: axisLockedYMaxType ?? axisYMax?.type ?? yMax.type,
                override: true
              }
            }
          };
        })
      : null;

  // 1. Remove axes not referenced by any series (the backend may send axes that
  //    have no corresponding data, e.g. position 1 unused while series use 0 & 2).
  // 2. Cap at 2 axes per side as a display safety net.
  // 3. Build a remap from original id → new array position so series yAxisIndex
  //    values stay valid after the two filtering steps.
  const yAxisIndexRemap = new Map<number, number>();
  let limitedMultiYAxis: typeof multiYAxis = null;
  if (multiYAxis) {
    const { filteredAxes, remap } = buildYAxisIndexRemap(multiYAxis, series);
    remap.forEach((v, k) => yAxisIndexRemap.set(k, v));
    limitedMultiYAxis = filteredAxes;
  }

  if (logAxis?.properties?.y?.active) {
    const yAxisLogOptions = getDefaultYAxisLogOptions(
      layout,
      lockedYMin,
      lockedYMax,
      EvaChart.RateDate,
      product,
      entityKind
    );
    Object.assign(yAxis, yAxisLogOptions);

    // Apply log options to each multi Y axis using per-axis locked values
    if (limitedMultiYAxis) {
      limitedMultiYAxis.forEach((axis) => {
        const perAxisLogOptions = getDefaultYAxisLogOptions(
          layout,
          axis.min,
          axis.max,
          EvaChart.RateDate,
          product,
          entityKind
        );
        Object.assign(axis, perAxisLogOptions);
        // Apply label formatter after min/max are finalized to suppress
        // power-of-10 labels that overlap a non-power-of-10 boundary
        axis.axisLabel = {
          ...axis.axisLabel,
          hideOverlap: true,
          formatter: createLogAxisLabelFormatter(axis.min, axis.max)
        };
      });
    }
  }

  let options = createEntityState(ChartOption.Chart, {
    chartType,
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title)
      })
    },
    yAxis: (limitedMultiYAxis as YAXisOption[]) ?? {
      ...createEntityState(ChartOption.YAxis, {
        ...yAxis,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: true,
          min: {
            value: Math.floor(yMin.value),
            type: lockedYMinType ?? yMin.type,
            override: true
          },
          max: {
            value: Math.floor(yMax.value),
            type: lockedYMaxType ?? yMax.type,
            override: true
          }
        }
      })
    },
    xAxis: (multiXAxis as XAXisOption[]) ?? {
      ...createEntityState(ChartOption.XAxis, {
        ...xAxis,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        useY: false, // useY is managed at hd chart
        lock: {
          show: true,
          min: {
            value: xMin.value,
            type: lockedXMinType ?? xMin.type,
            format: xMin.format,
            override: true
          },
          max: {
            value: xMax.value,
            type: lockedXMaxType ?? xMax.type,
            format: xMax.format,
            override: true
          }
        }
      })
    },
    grid: { top: 46, bottom: allProducts.length > 0 ? 70 : 46, left: 62, right: 20 },
    ...(multiProduct?.active && {
      dataZoom: [
        { type: "inside", orient: "horizontal", filterMode: "none" },
        ...(!logAxis?.properties?.y?.active
          ? [{ type: "inside", orient: "vertical", filterMode: "none" } as const]
          : [])
      ]
    }),
    ...(overrideSeries && {
      series: series
        .filter((s) => (s.xAxisIndex ?? 0) !== -1 && (s.yAxisIndex ?? 0) !== -1)
        .map((location, index) => {
          const { label, style, x, y, groupTitle } = location;
          const data = _zip(x, y);
          const id =
            groupTitle !== label
              ? `${groupTitle}, ${label}, ${index}, ${uid()}`
              : `${groupTitle}, ${index}, ${uid()}`;

          const isPoint = data.length === 1;
          const multiPoint = data.length > 1;

          const isStatisticsSeries = isStatisticsLabel(label);
          const { thickness, opacity } = getSeriesStyle(label, style);

          // Determine line type - forecasts keep original logic, historical data uses product-based styling
          let lineType: string | number[];
          if (location.isForecast) {
            // Forecast series: keep original styling
            lineType = location.isComparisonForecast ? "dotted" : "dashed";
          } else {
            // Historical data: apply product-based line styles
            lineType = getLineStyleForSeries(productStyleMap, location, false, false);
          }

          const effectiveColor = getEffectiveSeriesColor(location, multiProduct);

          const lineSeries = createEntityState(ChartOption.LineSeries, {
            id,
            name: label,
            data: data,
            itemStyle: {
              color: effectiveColor
            },
            xAxisIndex: location.xAxisIndex,
            yAxisIndex: limitedMultiYAxis
              ? yAxisIndexRemap.get(location.yAxisIndex ?? 0) ?? 0
              : location.yAxisIndex,
            lineStyle: {
              width: thickness,
              color: effectiveColor,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              type: lineType as any,
              opacity: opacity
            },
            z: isStatisticsSeries ? STATISTICS_Z_INDEX : DEFAULT_Z_INDEX,
            ...(isPoint
              ? {
                  symbolSize: 5
                }
              : {}),
            ...(isPoint ? { symbol: "circle" } : {}),
            showSymbol: !multiPoint || isPoint,
            // TODO chart: add proper typing
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            markLine: getMarkLineData(
              location.annotations,
              logAxis?.properties?.y?.active ? 0.01 : 0
            )
          });

          return lineSeries;
        })
    })
  });

  if (typeWellSeries.length > 0) {
    options = updateOptionsWithTypeWellsSeries(options, typeWellSeries);
  }

  options = applyProductLegend(options, multiProduct);

  return options;
}
